import React, { useEffect, useState, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faLayerGroup,
    faAlignLeft,
    faParagraph,
    faInfoCircle,
    faImage,
    faHashtag,
    faUserGraduate,
    faLanguage,
    faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import InputWithLabel from 'components/InputWithLabel/InputWithLabel';
import Button from 'components/Button/Button';
import TranslationLayout from '../TranslationLayout/TranslationLayout';
import { moduleSchema, ModuleFormValues } from '../../types/subjectSchemas';
import { subjectApi } from '../../api/subjectApi';
import { moduleApi } from '../../api/moduleApi';
import { Module } from '../../types/subject';
import { LANGUAGES } from '../../constants/languages';
import styles from '../SubjectForm/SubjectForm.module.css';

interface ModuleFormProps {
    mode: 'add' | 'edit';
}

const ModuleForm: React.FC<ModuleFormProps> = ({ mode }) => {
    const { subjectSlug, moduleSlug } = useParams<{ subjectSlug: string; moduleSlug: string }>();
    const [subjectUUID, setSubjectUUID] = useState<string | null>(null);
    const [moduleUUID, setModuleUUID] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [selectedLang, setSelectedLang] = useState(LANGUAGES[0]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        setValue,
        reset,
        control,
        formState: { errors, isSubmitting },
    } = useForm<ModuleFormValues>({
        resolver: zodResolver(moduleSchema),
        defaultValues: {
            moduleId: '',
            ageGroup: 0,
            name: '',
            shortDescription: '',
            longDescription: '',
            contentCompanyName: '',
            level: '',
            translations: LANGUAGES.reduce((acc, lang) => ({
                ...acc,
                [lang.code]: { name: '', short_description: '', long_description: '' }
            }), {})
        }
    });

    const translations = useWatch({ control, name: 'translations' }) as Record<string, any> || {};

    useEffect(() => {
        const loadSubject = async () => {
            if (subjectSlug) {
                try {
                    const response = await subjectApi.getBySlug(subjectSlug);
                    if (response.data) {
                        setSubjectUUID(response.data.id);
                    }
                } catch (error) {
                    console.error('Error loading subject for module form:', error);
                }
            }
        };
        loadSubject();
    }, [subjectSlug]);

    useEffect(() => {
        const loadModule = async () => {
            if (mode === 'edit' && moduleSlug) {
                try {
                    const response = await moduleApi.getBySlug(moduleSlug);
                    const module = response.data;
                    if (module) {
                        setModuleUUID(module.id);
                        reset({
                            moduleId: (module as any).moduleId || module.id,
                            slug: module.slug,
                            ageGroup: module.age_group || 0,
                            name: module.name,
                            shortDescription: module.short_description || '',
                            longDescription: module.long_description || '',
                            contentCompanyName: module.content_company_name || '',
                            level: (module.level as any) || '',
                            translations: LANGUAGES.reduce((acc, lang) => ({
                                ...acc,
                                [lang.code]: module.translations?.[lang.code] || { name: '', short_description: '', long_description: '' }
                            }), {})
                        });
                        setPreviewUrl(module.thumbnail_url || null);
                    }
                } catch (error) {
                    console.error('Error loading module:', error);
                }
            }
        };
        loadModule();
    }, [mode, moduleSlug, reset]);

    const handleFileSelect = (file: File) => {
        if (file) {
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            setValue('thumbnail', file, { shouldValidate: true });
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleFileSelect(e.target.files[0]);
        }
    };

    const completedCount = Object.values(translations).filter(
        (t: any) => t?.name && t.name.trim().length > 0
    ).length;

    const [isUploading, setIsUploading] = useState(false);

    const onSubmit = async (data: ModuleFormValues) => {
        if (!subjectUUID) {
            alert('Subject information not loaded yet.');
            return;
        }

        try {
            setIsUploading(true);
            let thumbnailUrl = previewUrl && !previewUrl.startsWith('blob:') ? previewUrl : undefined;
            if (data.thumbnail instanceof File) {
                const { uploadImage } = await import('api/upload');
                thumbnailUrl = await uploadImage(data.thumbnail, 'modules');
            }

            const payload: Partial<Module> = {
                name: data.name,
                subject_id: subjectUUID,
                age_group: data.ageGroup,
                short_description: data.shortDescription,
                long_description: data.longDescription,
                content_company_name: data.contentCompanyName,
                level: data.level,
                translations: data.translations,
                thumbnail_url: thumbnailUrl,
            };

            if (mode === 'add') {
                payload.id = data.moduleId;
                await moduleApi.create(payload);
                alert('Module added successfully!');
            } else if (moduleUUID) {
                await moduleApi.update(moduleUUID, payload);
                alert('Module updated successfully!');
            }
            navigate(`/subject/${subjectSlug}`);
        } catch (error) {
            console.error(`Failed to ${mode} module:`, error);
            alert(`Failed to ${mode} module`);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className={styles.pageContainer}>
            <header className={styles.header}>
                <h1 className={styles.title}>{mode === 'add' ? 'Add New Module' : 'Edit Module'}</h1>
                <p className={styles.subtitle}>
                    {mode === 'add' ? 'Create a new learning module for this subject.' : 'Update the module details.'}
                </p>
            </header>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <FontAwesomeIcon icon={faInfoCircle} className={`${styles.sectionIcon} ${styles.sectionIconPrimary}`} />
                        <h2 className={styles.sectionTitle}>Module Details</h2>
                    </div>

                    <div className={styles.sectionContent}>
                        <div className={styles.imageUploadGrid} style={{ gridTemplateColumns: '1fr', maxWidth: '600px', margin: '0 auto 2.5rem' }}>
                            <div className={styles.uploadContainer}>
                                <label className={styles.uploadLabel}>Module Thumbnail</label>
                                <div
                                    className={styles.thumbnailPlaceholder}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    {previewUrl ? (
                                        <img src={previewUrl} alt="Preview" className={styles.previewImage} />
                                    ) : (
                                        <div className={styles.placeholderContent}>
                                            <FontAwesomeIcon icon={faImage} className={styles.placeholderIcon} />
                                            <span className={styles.placeholderText}>Click to upload thumbnail</span>
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        style={{ display: 'none' }}
                                        onChange={handleFileInputChange}
                                        accept="image/*"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={styles.imageUploadGrid} style={{ marginBottom: '2rem' }}>
                            <InputWithLabel
                                label="Module ID"
                                placeholder="e.g. MOD-001"
                                icon={<FontAwesomeIcon icon={faHashtag} />}
                                error={errors.moduleId?.message}
                                {...register('moduleId')}
                                readOnly={mode === 'edit'}
                                style={mode === 'edit' ? { backgroundColor: '#F3F4F6', cursor: 'not-allowed' } : {}}
                            />
                            {mode === 'edit' &&
                                <InputWithLabel
                                    label="URL Slug"
                                    placeholder="e.g. intro-to-algebra"
                                    error={errors.slug?.message}
                                    {...register('slug')}
                                    readOnly
                                    style={{ backgroundColor: '#F3F4F6', cursor: 'not-allowed' }}
                                />
                            }
                            <InputWithLabel
                                label="Recommended Age Group"
                                type="number"
                                placeholder="e.g. 12"
                                icon={<FontAwesomeIcon icon={faUserGraduate} />}
                                error={errors.ageGroup?.message}
                                {...register('ageGroup', { valueAsNumber: true })}
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <InputWithLabel
                                label="Module Name"
                                placeholder="e.g. Introduction to Algebra"
                                error={errors.name?.message}
                                {...register('name')}
                            />
                        </div>

                        <div className={styles.fieldGroup} style={{ marginTop: '1.5rem' }}>
                            <InputWithLabel
                                label="Short Description"
                                placeholder="A brief summary for the card view"
                                error={errors.shortDescription?.message}
                                {...register('shortDescription')}
                            />
                        </div>

                        <div className={styles.imageUploadGrid} style={{ marginTop: '1.5rem', marginBottom: '0' }}>
                            <InputWithLabel
                                label="Content Company Name"
                                placeholder="e.g. Sensible-VR"
                                error={errors.contentCompanyName?.message}
                                {...register('contentCompanyName')}
                            />
                            
                            <div className={styles.transField}>
                                <label className={styles.transLabel}>Level</label>
                                <select 
                                    className={styles.selectField} 
                                    {...register('level')}
                                >
                                    <option value="">Select Level</option>
                                    <option value="Fundamental">Fundamental</option>
                                    <option value="Basic">Basic</option>
                                    <option value="Intermediate">Intermediate</option>
                                    <option value="Advanced">Advanced</option>
                                </select>
                                {errors.level && (
                                    <span className={styles.errorText}>{errors.level.message}</span>
                                )}
                            </div>
                        </div>

                        <div className={styles.transField} style={{ marginTop: '1.5rem' }}>
                            <label className={styles.transLabel}>Long Description</label>
                            <textarea
                                className={styles.transTextarea}
                                placeholder="Detailed description of the module content..."
                                {...register('longDescription')}
                                style={{ minHeight: '120px' }}
                            />
                            {errors.longDescription && (
                                <span className={styles.errorText}>{errors.longDescription.message}</span>
                            )}
                        </div>
                    </div>
                </div>

                <TranslationLayout
                    languages={LANGUAGES}
                    selectedLang={selectedLang}
                    onLangSelect={setSelectedLang}
                    translations={translations}
                    completedCount={completedCount}
                >
                    <div className={styles.transField}>
                        <InputWithLabel
                            label={`Module Name in ${selectedLang.name}`}
                            placeholder="Enter translated module name"
                            labelClassName={styles.transLabel}
                            {...register(`translations.${selectedLang.code}.name` as any)}
                        />
                    </div>

                    <div className={styles.transField} style={{ marginTop: '1.5rem' }}>
                        <InputWithLabel
                            label={`Short Description in ${selectedLang.name}`}
                            placeholder="Enter translated short description"
                            labelClassName={styles.transLabel}
                            {...register(`translations.${selectedLang.code}.short_description` as any)}
                        />
                    </div>

                    <div className={styles.transField} style={{ marginTop: '1.5rem' }}>
                        <label className={styles.transLabel}>Long Description in {selectedLang.name}</label>
                        <textarea
                            className={styles.transTextarea}
                            placeholder="Enter translated detailed description..."
                            {...register(`translations.${selectedLang.code}.long_description` as any)}
                            style={{ minHeight: '120px' }}
                        />
                    </div>
                </TranslationLayout>

                <div className={styles.formActions}>
                    <Button variant="ghost" type="button" onClick={() => navigate(`/subject/${subjectSlug}`)}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" isLoading={isSubmitting || isUploading}>
                        {mode === 'add' ? 'Create Module' : 'Save Changes'}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default ModuleForm;
